import {
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInSeconds,
    differenceInWeeks,
    differenceInYears,
    format,
    isThisYear,
    isToday,
    isTomorrow,
    isYesterday,
} from 'date-fns';
import i18n from 'i18next';

// Mixed relative and absolute time format
// For today, show relative time (Now, 5 minutes ago, 2 hours ago)
// For +/- 1 day, show relative day (Today, Yesterday, Tomorrow)
// For this year, show absolute date (Jan 1, Feb 2)
// For other years, show absolute date with year (Jan 1, 2020, Feb 2, 2020)
export const formatTimeMixed = (date: Date | string, showDayLevelOnly?: boolean) => {
    if (!date) return null;

    const formattedDate = new Date(date);
    const currentTime = new Date();

    const minutesDifference = differenceInMinutes(currentTime, formattedDate);
    const hoursDifference = differenceInHours(currentTime, formattedDate);
    const daysDifference = differenceInDays(currentTime, formattedDate);

    if (isToday(formattedDate)) {
        if (!showDayLevelOnly) {
            // If not restricted to day-level granularity, show now/minutes/hours
            if (minutesDifference <= 1) {
                return i18n.t('Time.Now');
            }
            if (minutesDifference <= 59) {
                return i18n.t('Time.MinutesAgo', { count: minutesDifference });
            }
            if (hoursDifference <= 12) {
                return i18n.t('Time.HoursAgo', { count: hoursDifference });
            }
        } else {
            return i18n.t('Time.Today');
        }
    }

    if (isTomorrow(formattedDate)) {
        return i18n.t('Time.Tomorrow');
    }

    if (isYesterday(formattedDate)) {
        return i18n.t('Time.Yesterday');
    }

    if (daysDifference >= 2) {
        return format(formattedDate, 'MMMM dd');
    }

    if (!isThisYear(formattedDate)) {
        return format(formattedDate, 'dd MMM yyyy');
    }
};

// Relative time format:
// For < 1 minute, show seconds (5 seconds ago or 5s)
// For < 1 hour, show minutes (5 minutes ago or 5m)
// For < 1 day, show hours (5 hours ago or 5h)
// For < 1 week, show days (5 days ago or 5d)
// For < 1 year, show weeks (5 weeks ago or 5w)
// For >= 1 year, show years (5 years ago or 5y)
export const formatTimeRelative = (date: Date | string, short?: boolean) => {
    if (!date) return null;

    const formattedDate = new Date(date);
    const currentTime = new Date();

    const secondsDifference = differenceInSeconds(currentTime, formattedDate);
    const minutesDifference = differenceInMinutes(currentTime, formattedDate);
    const hoursDifference = differenceInHours(currentTime, formattedDate);
    const daysDifference = differenceInDays(currentTime, formattedDate);
    const weeksDifference = differenceInWeeks(currentTime, formattedDate);
    const yearsDifference = differenceInYears(currentTime, formattedDate);

    if (secondsDifference === 0) {
        return i18n.t('Time.Now');
    } else if (short) {
        if (secondsDifference < 60) {
            return i18n.t('Time.SecondsShort', { count: secondsDifference });
        }
        if (minutesDifference < 60) {
            return i18n.t('Time.MinutesShort', { count: minutesDifference });
        }
        if (hoursDifference < 24) {
            return i18n.t('Time.HoursShort', { count: hoursDifference });
        }
        if (daysDifference < 7) {
            return i18n.t('Time.DaysShort', { count: daysDifference });
        }
        if (weeksDifference < 52) {
            return i18n.t('Time.WeeksShort', { count: weeksDifference });
        }

        return i18n.t('Time.YearsShort', { count: yearsDifference });
    } else {
        if (secondsDifference < 60) {
            return i18n.t('Time.SecondsAgo', { count: secondsDifference });
        }
        if (minutesDifference < 60) {
            return i18n.t('Time.MinutesAgo', { count: minutesDifference });
        }
        if (hoursDifference < 24) {
            return i18n.t('Time.HoursAgo', { count: hoursDifference });
        }
        if (daysDifference < 7) {
            return i18n.t('Time.DaysAgo', { count: daysDifference });
        }
        if (weeksDifference < 52) {
            return i18n.t('Time.WeeksAgo', { count: weeksDifference });
        }

        return i18n.t('Time.YearsAgo', { count: yearsDifference });
    }
};

export const formatDateTime = (date: Date | string) => {
    if (!date) return null;

    const formattedDate = new Date(date);

    return format(formattedDate, 'dd MMM yyyy, HH:mm');
};

export const isOlderThanSecs = (date: Date | string, seconds: number) => {
    if (!date) return false;

    const formattedDate = new Date(date);
    const currentTime = new Date();

    return differenceInSeconds(currentTime, formattedDate) > seconds;
};
